import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useRBAC } from "../contexts/RBACContext";
import Navbar from "../components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Send, Clock, Paperclip, MessageSquare, Plus, X, Download, Trash2, Key, Mail, Shield, Monitor, Users, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function TicketDetail({ user, logout, previewRole, setPreviewRole, previewDealership, setPreviewDealership, previewDepartment, setPreviewDepartment }) {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useRBAC();
  
  // Use preview role if admin is previewing, otherwise use actual role
  const effectiveRole = previewRole || user.role;
  
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [itStaff, setItStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  
  // Comment form
  const [newComment, setNewComment] = useState("");
  const [isPrivateComment, setIsPrivateComment] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Comment edit/delete
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState("");
  
  // Time entry form
  const [timeEntry, setTimeEntry] = useState({ time_spent: "", description: "" });
  const [submittingTime, setSubmittingTime] = useState(false);
  
  // Update form
  const [updateData, setUpdateData] = useState({
    status: "new",
    priority: "medium",
    assigned_to: "unassigned",
    additional_emails: []
  });

  // Send Credentials state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsData, setCredentialsData] = useState({
    recipient_email: "",
    credentials_text: "",
    expiration_type: "24_hours"
  });
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [sentCredentials, setSentCredentials] = useState([]);

  // Dynamic RBAC permission checks - computed after ticket loads
  // Check if user is the ticket creator
  const isTicketCreator = ticket?.created_by === user.id;
  
  // End users can comment on their own tickets, others follow RBAC
  const canAddComments = hasPermission('ticket_comment', effectiveRole) || (effectiveRole === 'end_user' && isTicketCreator);
  const canUpdateTicket = hasPermission('ticket_update', effectiveRole);
  const canAssignTickets = hasPermission('ticket_assign', effectiveRole);
  const canSendCredentials = hasPermission('credentials_send', effectiveRole);
  // Private comments permission - only Super Admin and Admin by default
  const canViewPrivateComments = hasPermission('ticket_private_comment', effectiveRole);
  const canCreatePrivateComments = hasPermission('ticket_private_comment', effectiveRole);
  // canDelete is typically only for admin/super_admin - use settings permission as proxy
  const canDelete = hasPermission('settings_users', effectiveRole);
  const canEditDeleteComments = effectiveRole === 'super_admin' || effectiveRole === 'admin';
  // Alias for backward compatibility
  const canUpdate = canUpdateTicket;

  useEffect(() => {
    fetchTicketDetails();
    if (user.role !== "end_user") {
      fetchItStaff();
      fetchSentCredentials();
    }
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      // Base requests for all users
      const requests = [
        axios.get(`${API}/tickets/${ticketId}`),
        axios.get(`${API}/tickets/${ticketId}/comments`)
      ];
      
      // Only fetch time entries for non-end-users
      if (user.role !== "end_user") {
        requests.push(axios.get(`${API}/tickets/${ticketId}/time-entries`));
      }
      
      const responses = await Promise.all(requests);
      
      setTicket(responses[0].data);
      setComments(responses[1].data);
      
      // Set time entries only if we fetched them
      if (user.role !== "end_user" && responses[2]) {
        setTimeEntries(responses[2].data);
      }
      
      setUpdateData({
        status: responses[0].data.status,
        priority: responses[0].data.priority,
        assigned_to: responses[0].data.assigned_to || "unassigned",
        additional_emails: responses[0].data.additional_emails || []
      });
    } catch (error) {
      toast.error("Failed to load ticket details");
      navigate("/tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchItStaff = async () => {
    try {
      const response = await axios.get(`${API}/users/it-staff`);
      setItStaff(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const fetchSentCredentials = async () => {
    try {
      const response = await axios.get(`${API}/secure-credentials/ticket/${ticketId}`);
      setSentCredentials(response.data);
    } catch (e) { console.error("Non-critical error:", e.message); }
  };

  const handleSendCredentials = async (e) => {
    e.preventDefault();
    if (!credentialsData.recipient_email || !credentialsData.credentials_text) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSendingCredentials(true);
    try {
      await axios.post(`${API}/secure-credentials`, {
        ticket_id: ticketId,
        recipient_email: credentialsData.recipient_email,
        credentials_text: credentialsData.credentials_text,
        expiration_type: credentialsData.expiration_type
      });
      toast.success(`Credentials sent to ${credentialsData.recipient_email}`);
      setShowCredentialsModal(false);
      setCredentialsData({
        recipient_email: "",
        credentials_text: "",
        expiration_type: "24_hours"
      });
      fetchSentCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send credentials");
    } finally {
      setSendingCredentials(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (!window.confirm("Are you sure you want to delete this credential link?")) {
      return;
    }
    try {
      await axios.delete(`${API}/secure-credentials/${credentialId}`);
      toast.success("Credential link deleted");
      fetchSentCredentials();
    } catch (error) {
      toast.error("Failed to delete credential");
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      await axios.post(`${API}/tickets/${ticketId}/comments`, {
        content: newComment,
        is_private: isPrivateComment
      });
      setNewComment("");
      setIsPrivateComment(false);
      toast.success("Comment added");
      fetchTicketDetails();
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentContent.trim()) return;
    try {
      await axios.patch(`${API}/tickets/${ticketId}/comments/${commentId}`, { content: editCommentContent });
      setEditingCommentId(null);
      setEditCommentContent("");
      toast.success("Comment updated");
      fetchTicketDetails();
    } catch (error) {
      toast.error("Failed to update comment");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await axios.delete(`${API}/tickets/${ticketId}/comments/${commentId}`);
      toast.success("Comment deleted");
      fetchTicketDetails();
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleAddTimeEntry = async (e) => {
    e.preventDefault();
    if (!timeEntry.time_spent || !timeEntry.description) return;
    
    setSubmittingTime(true);
    try {
      await axios.post(`${API}/tickets/${ticketId}/time-entries`, {
        time_spent: parseInt(timeEntry.time_spent),
        description: timeEntry.description
      });
      setTimeEntry({ time_spent: "", description: "" });
      toast.success("Time logged");
      fetchTicketDetails();
    } catch (error) {
      toast.error("Failed to log time");
    } finally {
      setSubmittingTime(false);
    }
  };

  const handleUpdateTicket = async () => {
    try {
      await axios.patch(`${API}/tickets/${ticketId}`, updateData);
      toast.success("Ticket updated");
      fetchTicketDetails();
    } catch (error) {
      toast.error("Failed to update ticket");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/tickets/${ticketId}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("File uploaded");
      fetchTicketDetails();
    } catch (error) {
      toast.error("Failed to upload file");
    }
  };

  const addEmailField = () => {
    setUpdateData(prev => ({
      ...prev,
      additional_emails: [...prev.additional_emails, ""]
    }));
  };

  const updateEmailField = (index, value) => {
    setUpdateData(prev => {
      const newEmails = [...prev.additional_emails];
      newEmails[index] = value;
      return { ...prev, additional_emails: newEmails };
    });
  };

  const removeEmailField = (index) => {
    setUpdateData(prev => ({
      ...prev,
      additional_emails: prev.additional_emails.filter((_, i) => i !== index)
    }));
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm(`Are you sure you want to delete ticket ${ticket.ticket_number}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setDeleting(true);
      await axios.delete(`${API}/tickets/${ticketId}`);
      toast.success(`Ticket ${ticket.ticket_number} has been deleted`);
      navigate("/tickets");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete ticket");
    } finally {
      setDeleting(false);
    }
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      low: "bg-green-100 text-green-700",
      medium: "bg-yellow-100 text-yellow-700",
      high: "bg-orange-100 text-orange-700",
      critical: "bg-red-100 text-red-700"
    };
    return classes[priority] || classes.medium;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      new: "bg-blue-100 text-blue-700",
      in_progress: "bg-purple-100 text-purple-700",
      pending: "bg-yellow-100 text-yellow-700",
      closed: "bg-gray-100 text-gray-700"
    };
    return classes[status] || classes.new;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
        <Navbar user={user} logout={logout} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-slate-60 dark:text-white0">Loading ticket...</div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return null;
  }

  // These now use the dynamic RBAC permissions defined at the top

  // Status and Priority options to match HR
  const STATUS_OPTIONS = [
    { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
    { value: "in_progress", label: "In Progress", color: "bg-purple-100 text-purple-800" },
    { value: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-800" },
    { value: "closed", label: "Closed", color: "bg-gray-100 text-gray-800" }
  ];

  const PRIORITY_OPTIONS = [
    { value: "low", label: "Low", color: "bg-green-100 text-green-700" },
    { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
    { value: "high", label: "High", color: "bg-orange-100 text-orange-700" },
    { value: "critical", label: "Critical", color: "bg-red-100 text-red-700" }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      "Hardware": "bg-blue-500",
      "Software": "bg-purple-500",
      "Network": "bg-green-500",
      "Email": "bg-orange-500",
      "Phone": "bg-teal-500",
      "Other": "bg-gray-500"
    };
    return colors[category] || "bg-blue-500";
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#252525]">
      <Navbar user={user} logout={logout} effectiveRole={effectiveRole} previewRole={previewRole} setPreviewRole={setPreviewRole} previewDealership={previewDealership} setPreviewDealership={setPreviewDealership} previewDepartment={previewDepartment} setPreviewDepartment={setPreviewDepartment} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/tickets")}
            data-testid="back-to-tickets-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tickets
          </Button>
          
          {canDelete && (
            <Button
              variant="destructive"
              onClick={handleDeleteTicket}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-ticket-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? "Deleting..." : "Delete Ticket"}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket Header - Mirroring HR Style */}
            <Card className="border-0 shadow-md">
              <CardHeader className="border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full ${getCategoryColor(ticket.category)} flex items-center justify-center`}>
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <span className="font-mono text-lg font-bold text-slate-700 dark:text-white" data-testid="ticket-number">
                          {ticket.ticket_number}
                        </span>
                        {ticket.category && (
                          <Badge className={`ml-3 ${getCategoryColor(ticket.category)} text-white`}>
                            {ticket.category}
                          </Badge>
                        )}
                        <Badge className={`ml-2 ${getStatusBadgeClass(ticket.status)}`}>
                          {ticket.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2" data-testid="ticket-title">
                      {ticket.title}
                    </h1>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                      <span>Submitted by: <strong>{ticket.created_by_name}</strong></span>
                      <span>•</span>
                      <span>{new Date(ticket.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Category</p>
                        <p className="font-medium text-slate-800 dark:text-white">{ticket.category || "General"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Dealership</p>
                        <p className="font-medium text-slate-800 dark:text-white">{ticket.dealership || "N/A"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Last Updated</p>
                        <p className="font-medium text-slate-800 dark:text-white">
                          {new Date(ticket.updated_at || ticket.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Paperclip className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Department</p>
                        <p className="font-medium text-slate-800 dark:text-white">{ticket.department || "N/A"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Send className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Assigned To</p>
                        <p className="font-medium text-slate-800 dark:text-white">{ticket.assigned_to_name || "Unassigned"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Time Logged</p>
                        <p className="font-medium text-slate-800 dark:text-white">{ticket.time_elapsed || 0} minutes</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Computer Name */}
                {ticket.computer_name && (
                  <div className="flex items-center gap-3 mt-4">
                    <Monitor className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Computer Name</p>
                      <p className="font-medium text-slate-800 dark:text-white">{ticket.computer_name}</p>
                    </div>
                  </div>
                )}

                {/* Additional Users */}
                {ticket.additional_emails && ticket.additional_emails.length > 0 && (
                  <div className="flex items-center gap-3 mt-4">
                    <Users className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Additional Users</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {ticket.additional_emails.map((email) => (
                          <span key={email} className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-300 rounded-md flex items-center gap-1">
                            {email}
                            {canUpdateTicket && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const updated = ticket.additional_emails.filter(e => e !== email);
                                  try {
                                    await axios.patch(`${API}/tickets/${ticketId}`, { additional_emails: updated });
                                    toast.success(`Removed ${email}`);
                                    fetchTicketDetails();
                                  } catch (err) {
                                    toast.error("Failed to remove email");
                                  }
                                }}
                                className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                                title={`Remove ${email}`}
                                data-testid={`remove-email-${email}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                <Separator className="my-6" />
                <div>
                  <h3 className="font-semibold text-slate-700 dark:text-white mb-2">Description</h3>
                  <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-4 rounded-lg" data-testid="ticket-description">
                    {ticket.description}
                  </p>
                </div>

                {/* Attachments Section - After Description */}
                <Separator className="my-6" />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-slate-700 dark:text-white flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Attachments {ticket.attachments?.length ? `(${ticket.attachments.length})` : ''}
                    </h3>
                    <label className="cursor-pointer" data-testid="add-attachment-btn">
                      <input type="file" className="hidden" onChange={handleFileUpload} />
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-md transition-colors">
                        <Plus className="w-4 h-4" />
                        Add Attachment
                      </span>
                    </label>
                  </div>
                  {ticket.attachments && ticket.attachments.length > 0 ? (
                    <div className="space-y-2">
                      {ticket.attachments.map((attachment, index) => (
                        <div key={attachment.id || `att-${index}`} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Paperclip className="w-4 h-4 text-slate-400" />
                            <div>
                              <span className="text-sm text-slate-700 dark:text-white">{attachment.filename}</span>
                              {attachment.size && (
                                <span className="text-xs text-slate-500 ml-2">
                                  ({attachment.size < 1024 * 1024 
                                    ? `${(attachment.size / 1024).toFixed(1)} KB` 
                                    : `${(attachment.size / (1024 * 1024)).toFixed(1)} MB`})
                                </span>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" asChild>
                            <a href={`${API}/tickets/${ticket.id}/attachments/${attachment.id}`} target="_blank" rel="noopener noreferrer">
                              <Download className="w-4 h-4" />
                            </a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 dark:text-slate-500">No attachments yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Activity & Notes Section - Mirroring HR Style */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Activity & Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Add Comment Form */}
                {canAddComments && (
                  <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    {/* Only show internal note toggle if user has permission */}
                    {canCreatePrivateComments && (
                      <div className="flex items-center gap-2 mb-3">
                        <Switch
                          id="private-comment"
                          checked={isPrivateComment}
                          onCheckedChange={setIsPrivateComment}
                        />
                        <Label htmlFor="private-comment" className="text-sm">Internal note (not visible to requester)</Label>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Textarea
                        placeholder={canCreatePrivateComments ? "Add a comment or internal note..." : "Add a comment..."}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className="flex-1"
                        rows={3}
                        data-testid="comment-input"
                      />
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        onClick={handleAddComment}
                        disabled={submittingComment || !newComment.trim()}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="add-comment-button"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {submittingComment ? "Sending..." : "Add Note"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Comments List - filter out private comments if user doesn't have permission */}
                <div className="space-y-4">
                  {comments.filter(c => !c.is_private || canViewPrivateComments).length > 0 ? (
                    comments.filter(c => !c.is_private || canViewPrivateComments).map((comment, index) => (
                      <div
                        key={comment.id || `comment-${index}`}
                        className={`p-4 rounded-lg ${
                          comment.is_private 
                            ? "bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400" 
                            : "bg-slate-50 dark:bg-slate-800"
                        }`}
                        data-testid={`comment-${index}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-slate-800 dark:text-white">{comment.user_name}</span>
                          {comment.is_private && (
                            <Badge variant="outline" className="text-yellow-600 border-yellow-400">
                              Internal
                            </Badge>
                          )}
                          {comment.edited_at && (
                            <span className="text-xs text-slate-400 italic">(edited)</span>
                          )}
                          <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                          {canEditDeleteComments && (
                            <div className="flex items-center gap-1 ml-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingCommentId(comment.id); setEditCommentContent(comment.content); }}
                                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-400 hover:text-blue-600 transition-colors"
                                title="Edit comment"
                                data-testid={`edit-comment-${comment.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteComment(comment.id); }}
                                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-400 hover:text-red-600 transition-colors"
                                title="Delete comment"
                                data-testid={`delete-comment-${comment.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        {editingCommentId === comment.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editCommentContent}
                              onChange={(e) => setEditCommentContent(e.target.value)}
                              className="min-h-[60px] text-sm"
                              data-testid={`edit-comment-input-${comment.id}`}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleEditComment(comment.id)} data-testid={`save-comment-${comment.id}`}>Save</Button>
                              <Button size="sm" variant="ghost" onClick={() => { setEditingCommentId(null); setEditCommentContent(""); }}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{comment.content}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-slate-500 py-8">No activity notes yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Update Ticket */}
            {canUpdate && (
              <Card className="border-0 shadow-md">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle>Update Ticket</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={updateData.status}
                      onValueChange={(value) => setUpdateData(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={updateData.priority}
                      onValueChange={(value) => setUpdateData(prev => ({ ...prev, priority: value }))}
                    >
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((priority) => (
                          <SelectItem key={priority.value} value={priority.value}>
                            {priority.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="assigned">Assign To</Label>
                    <Select
                      value={updateData.assigned_to || "unassigned"}
                      onValueChange={(value) => setUpdateData(prev => ({ ...prev, assigned_to: value === "unassigned" ? null : value }))}
                    >
                      <SelectTrigger data-testid="assigned-select">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {itStaff.map((staff) => (
                          <SelectItem key={staff.id} value={staff.id}>
                            {staff.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Additional Emails</Label>
                      <Button variant="ghost" size="sm" onClick={addEmailField} className="text-blue-600 hover:text-blue-700 h-7 px-2" data-testid="add-email-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    {updateData.additional_emails.map((email, index) => (
                      <div key={`email-field-${index}`} className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="email@example.com"
                          value={email}
                          onChange={(e) => updateEmailField(index, e.target.value)}
                          className="flex-1"
                          data-testid={`email-input-${index}`}
                        />
                        <Button variant="ghost" size="icon" onClick={() => removeEmailField(index)} className="text-red-500 hover:text-red-700 h-9 w-9" data-testid={`remove-email-btn-${index}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={handleUpdateTicket}
                    data-testid="update-ticket-button"
                  >
                    Update Ticket
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Send Credentials - Sidebar Card */}
            {canSendCredentials && (
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Secure Credentials
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => setShowCredentialsModal(true)}
                    className="w-full bg-[#7a1d42] hover:bg-[#5a1632]"
                    data-testid="send-credentials-button"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Send Credentials
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    Securely send login credentials to a recipient via encrypted link.
                  </p>
                  
                  {/* Sent Credentials History */}
                  {sentCredentials.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm font-medium text-slate-700 dark:text-white mb-2">Recently Sent</p>
                      <div className="space-y-2">
                        {sentCredentials.slice(0, 3).map((cred, index) => (
                          <div key={cred.id || `cred-${index}`} className="text-xs p-2 bg-slate-50 dark:bg-slate-800 rounded">
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3 text-slate-400" />
                              <span className="text-slate-600 dark:text-slate-300 truncate">{cred.recipient_email}</span>
                            </div>
                            <div className="text-slate-400 mt-1">
                              {new Date(cred.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Ticket Summary */}
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg">Ticket Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Category</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`w-6 h-6 rounded-full ${getCategoryColor(ticket.category)} flex items-center justify-center`}>
                      <MessageSquare className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium text-slate-800 dark:text-white">{ticket.category || "General"}</span>
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Priority</p>
                  <Badge className={`mt-1 ${getPriorityBadgeClass(ticket.priority)}`}>
                    {ticket.priority}
                  </Badge>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Status</p>
                  <Badge className={`mt-1 ${getStatusBadgeClass(ticket.status)}`}>
                    {ticket.status.replace('_', ' ')}
                  </Badge>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Created</p>
                  <p className="font-medium text-slate-800 dark:text-white text-sm">
                    {new Date(ticket.created_at).toLocaleString()}
                  </p>
                </div>
                {ticket.assigned_to_name && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Assigned To</p>
                      <p className="font-medium text-slate-800 dark:text-white text-sm">
                        {ticket.assigned_to_name}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Send Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Send Secure Credentials
            </DialogTitle>
            <DialogDescription>
              Send encrypted credentials that will be accessible via a secure link.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient-email">Recipient Email</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="recipient@example.com"
                value={credentialsData.recipient_email}
                onChange={(e) => setCredentialsData(prev => ({ ...prev, recipient_email: e.target.value }))}
                data-testid="credentials-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="credentials-text">Credentials</Label>
              <Textarea
                id="credentials-text"
                placeholder="Enter username, password, or other credentials..."
                value={credentialsData.credentials_text}
                onChange={(e) => setCredentialsData(prev => ({ ...prev, credentials_text: e.target.value }))}
                rows={4}
                data-testid="credentials-text-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiration">Link Expiration</Label>
              <Select
                value={credentialsData.expiration_type}
                onValueChange={(value) => setCredentialsData(prev => ({ ...prev, expiration_type: value }))}
              >
                <SelectTrigger data-testid="credentials-expiration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time view (expires after viewing)</SelectItem>
                  <SelectItem value="24_hours">24 hours</SelectItem>
                  <SelectItem value="7_days">7 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendCredentials}
              disabled={sendingCredentials || !credentialsData.recipient_email || !credentialsData.credentials_text}
              className="bg-[#7a1d42] hover:bg-[#5a1632]"
              data-testid="send-credentials-confirm-button"
            >
              {sendingCredentials ? "Sending..." : "Send Credentials"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}